
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { SEOHead } from "@/components/seo/SEOHead";
import { useNursingHome } from "@/hooks/use-nursing-home";
import { ClaimDialog } from "@/components/nursing-homes/ClaimDialog";
import { EmailVerificationPrompt } from "@/components/auth/EmailVerificationPrompt";
import RegisterForm from "@/components/auth/register/RegisterForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/components/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import {
  emptyNursingHomeClaimForm,
  mergeClaimFormFromStorage,
  buildVerificationDocumentsPayload,
} from "@/lib/nursing-home-claim-form";
import type { ClaimSelectField } from "@/components/nursing-homes/ClaimDialog";

export default function ClaimNursingHomePage() {
  const { nursingHomeSlug } = useParams<{ nursingHomeSlug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [checkingClaim, setCheckingClaim] = useState(false);
  const [formData, setFormData] = useState(emptyNursingHomeClaimForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [claimSubmittedSuccessfully, setClaimSubmittedSuccessfully] = useState(false);
  const { data: nursingHome, isLoading, error } = useNursingHome(nursingHomeSlug);
  
  // Check for pending claim data when user arrives (especially after auth)
  useEffect(() => {
    const pendingClaimData = sessionStorage.getItem('pendingClaimData');
    const shouldShowRegistration = sessionStorage.getItem('showRegistrationInClaim');
    
    if (pendingClaimData && user) {
      try {
        const claimData = JSON.parse(pendingClaimData);
        setFormData(mergeClaimFormFromStorage(claimData.formData));
        setClaimDialogOpen(true);
        // Clear the stored data
        sessionStorage.removeItem('pendingClaimData');
        sessionStorage.removeItem('showRegistrationInClaim');
      } catch (error) {
        console.error('Error parsing pending claim data:', error);
        sessionStorage.removeItem('pendingClaimData');
        sessionStorage.removeItem('showRegistrationInClaim');
      }
    } else if (shouldShowRegistration && !user) {
      setShowRegistration(true);
    }
  }, [user]);
  
  useEffect(() => {
    // Show registration form immediately if user is not logged in
    if (nursingHome && !nursingHome.claimed && !user) {
      setShowRegistration(true);
    }
    // Only show claim dialog if user is logged in
    else if (nursingHome && !nursingHome.claimed && user && !showRegistration) {
      setClaimDialogOpen(true);
    }
  }, [nursingHome, user, showRegistration]);

  // Handle authentication redirect - ensure dialog opens when user arrives after auth
  useEffect(() => {
    if (user && nursingHome && !nursingHome.claimed) {
      // Check if we just completed auth and have pending claim data
      const justCompletedAuth = sessionStorage.getItem('justCompletedAuth');
      if (justCompletedAuth) {
        sessionStorage.removeItem('justCompletedAuth');
        // Dialog will be opened by the pending claim data effect above
      }
    }
  }, [user, nursingHome]);
  
  // If the nursing home is already claimed, show a different message
  if (nursingHome?.claimed) {
    return (
      <Layout>
        <SEOHead 
          title={`${nursingHome.center_name} - Already Claimed | Grandpal`}
          description="This nursing home profile has already been claimed."
        />
        <div className="container py-16 max-w-3xl">
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Profile Already Claimed</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center">
                The profile for <strong>{nursingHome.center_name}</strong> has already been claimed.
              </p>
              <p className="text-center text-muted-foreground">
                If you believe this is an error or you should have access to this profile, 
                please contact our support team.
              </p>
              <div className="flex justify-center pt-4">
                <Button asChild>
                  <Link to={`/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}`}>
                    View Nursing Home Profile
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  const onDialogOpenChange = (open: boolean) => {
    setClaimDialogOpen(open);
    // Only navigate to the nursing home page if we're not showing registration or email verification
    // and if the claim wasn't just submitted successfully
    if (!open && nursingHome && !showRegistration && !showEmailVerification && !claimSubmittedSuccessfully) {
      const nursingHomeUrl = `/nursing-homes/${nursingHome.county_slug}/${nursingHome.nh_slug}`;
      navigate(nursingHomeUrl);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSelectFieldChange = (field: ClaimSelectField, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "referralSource" && value === "unset" ? "" : value,
    }));
  };

  const patchFormData = (patch: Partial<typeof formData>) => {
    setFormData((prev) => ({ ...prev, ...patch }));
  };

  const handleClaimSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      if (!user) {
        toast.error("Please create an account first");
        return;
      }
      
      const centerName = nursingHome?.center_name ?? "";
      const verification_documents = buildVerificationDocumentsPayload(formData, centerName);

      const { error } = await supabase.from("nursing_home_claims").insert({
        nursing_home_id: nursingHomeSlug,
        user_id: user.id,
        status: "pending",
        verification_documents,
        notes: null,
      });

      if (error) throw error;
      
      toast.success("Your claim request has been submitted");
      
      // Mark claim as successfully submitted to prevent unwanted redirects
      setClaimSubmittedSuccessfully(true);
      
      // Check if user's email is confirmed
      if (!user.email_confirmed_at) {
        // Show email verification prompt instead of immediate redirect
        setShowEmailVerification(true);
        setClaimDialogOpen(false);
      } else {
        // Set flag for redirection after auth
        sessionStorage.setItem('justClaimedNursingHome', nursingHomeSlug || '');
        
        // Close dialog and redirect to owner dashboard
        setClaimDialogOpen(false);
        navigate('/owner/dashboard');
      }
      
    } catch (error) {
      console.error("Error submitting claim:", error);
      toast.error("Failed to submit claim");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEmailVerificationClose = () => {
    setShowEmailVerification(false);
    // Set flag for redirection after auth
    sessionStorage.setItem('justClaimedNursingHome', nursingHomeSlug || '');
    // Redirect to owner dashboard
    navigate('/owner/dashboard');
  };

  const handleShowRegistration = () => {
    // Store nursing home name for the owner welcome email
    if (nursingHome?.center_name) {
      sessionStorage.setItem('claimNursingHomeName', nursingHome.center_name);
    }
    // Store claim data for after registration
    const claimData = {
      nursingHomeSlug,
      formData
    };
    sessionStorage.setItem('pendingClaimData', JSON.stringify(claimData));
    sessionStorage.setItem('showRegistrationInClaim', 'true');
    setShowRegistration(true);
  };

  return (
    <Layout>
      <SEOHead 
        title={isLoading ? "Claim Nursing Home | Grandpal" : `Claim ${nursingHome?.center_name} | Grandpal`}
        description="Claim your nursing home profile on Grandpal to manage your information and connect with families."
      />

      <div className="container py-8 max-w-3xl">
        {isLoading || checkingClaim ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center justify-center text-center">
                <h2 className="text-2xl font-bold mb-2">Nursing Home Not Found</h2>
                <p className="text-muted-foreground mb-4">
                  We couldn't find the nursing home you're looking for. Please check the link and try again.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button asChild className="bg-grandpal-green-dark hover:bg-grandpal-green-darkHover">
                    <Link to="/owner/claim">Search to claim a home</Link>
                  </Button>
                  <Button asChild variant="outline">
                    <Link to="/nursing-homes">Public directory (families)</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : nursingHome ? (
          <>
            {showEmailVerification && user ? (
              <div className="flex justify-center">
                <EmailVerificationPrompt 
                  email={user.email || ""} 
                  onClose={handleEmailVerificationClose}
                />
              </div>
            ) : (
              <>
                {showRegistration ? (
                  <Card>
                    <CardHeader>
                      <CardTitle>Create Account to Claim Profile</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="mb-6 text-muted-foreground">
                        To claim <strong>{nursingHome.center_name}</strong>, you'll need to create an account. 
                        This will allow you to manage your nursing home profile and view your claim status.
                      </p>
                      <RegisterForm initialEmail={formData.email} fromClaim={true} />
                      <div className="mt-4 text-center">
                        <Button 
                          variant="outline" 
                          onClick={() => {
                            setShowRegistration(false);
                            sessionStorage.removeItem('showRegistrationInClaim');
                          }}
                        >
                          Back
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <>
                    <Card className="mb-6">
                      <CardHeader>
                        <CardTitle>Claim Your Nursing Home Profile</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="mb-4">
                          You're about to claim the profile for <strong>{nursingHome.center_name}</strong>. 
                          Claiming this profile will allow you to:
                        </p>
                        <ul className="list-disc pl-5 mb-4 space-y-1">
                          <li>Update your nursing home's information and photos</li>
                          <li>Respond to inquiries from families</li>
                          <li>Access qualified leads who are interested in your facility</li>
                          <li>Highlight special features and amenities</li>
                        </ul>
                        {user ? (
                          <Button 
                            onClick={() => setClaimDialogOpen(true)}
                            className="w-full mt-4 bg-grandpal-green-dark hover:bg-grandpal-green-darkHover"
                          >
                            Claim This Profile
                          </Button>
                        ) : (
                          <Button 
                            onClick={handleShowRegistration}
                            className="w-full mt-4 bg-grandpal-green-dark hover:bg-grandpal-green-darkHover"
                          >
                            Sign Up to Claim Profile
                          </Button>
                        )}
                      </CardContent>
                    </Card>

                    {user && (
                      <ClaimDialog
                        centerName={nursingHome.center_name}
                        open={claimDialogOpen}
                        onOpenChange={onDialogOpenChange}
                        isSubmitting={isSubmitting}
                        formData={formData}
                        onInputChange={handleInputChange}
                        onSelectFieldChange={onSelectFieldChange}
                        patchFormData={patchFormData}
                        onSubmit={handleClaimSubmit}
                        onShowRegistration={handleShowRegistration}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </>
        ) : null}
      </div>
    </Layout>
  );
}
